import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import dashboardService from '../../services/dashboardService';
import { FiFolder, FiCheckSquare, FiClock, FiDollarSign, FiTrendingUp, FiTrendingDown, FiAlertCircle } from 'react-icons/fi';
import { CURRENCY_SYMBOL } from '../../config/constants';

const ProjectDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
const Inr = CURRENCY_SYMBOL 

  useEffect(() => {
    loadData();
  }, [selectedProject]);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await dashboardService.getProjectDashboard(selectedProject);
      setData(result);
    } catch (error) {
      console.error('Error loading project dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading project dashboard...</div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const statusColors = {
      'Planning': 'badge-secondary',
      'In Progress': 'badge-primary',
      'On Hold': 'badge-warning',
      'Completed': 'badge-success',
      'Cancelled': 'badge-danger'
    };
    return statusColors[status] || 'badge-default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'text-red-600',
      'High': 'text-orange-600',
      'Medium': 'text-blue-600',
      'Low': 'text-green-600'
    };
    return colors[priority] || 'text-gray-600';
  };

  const calculateSummary = () => {
    const projects = data?.projects || [];
    return {
      total: projects.length,
      avgCompletion: projects.length > 0 
        ? Math.round(projects.reduce((sum, p) => sum + p.completionRate, 0) / projects.length)
        : 0,
      totalTasks: projects.reduce((sum, p) => sum + p.totalTasks, 0),
      completedTasks: projects.reduce((sum, p) => sum + p.completedTasks, 0),
      totalBudget: projects.reduce((sum, p) => sum + (p.budget || 0), 0),
      totalSpent: projects.reduce((sum, p) => sum + (p.actualCost || 0), 0)
    };
  };

  const summary = calculateSummary();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Project Dashboard</h1>
          <p className="text-gray-600 mt-1">Detailed view of all project metrics and performance</p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Projects</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{summary.total}</p>
            </div>
            <FiFolder className="w-12 h-12 text-blue-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Avg Completion</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{summary.avgCompletion}%</p>
            </div>
            <FiCheckSquare className="w-12 h-12 text-green-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Tasks</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{summary.totalTasks}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.completedTasks} completed</p>
            </div>
            <FiCheckSquare className="w-12 h-12 text-purple-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Budget Overview</p>
              <p className="text-2xl font-bold text-gray-900 mt-2 d-flex">
                 <span className="fs-2" style={{fontSize:"1.5rem", marginTop:"0.5rem" }} > <Inr /></span>{summary.totalBudget.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1 d-flex">
                 <span className="fs-2" > <Inr /></span>{summary.totalSpent.toLocaleString()} spent
              </p>
            </div>
            <Inr className="w-12 h-12 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Project Details</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tasks
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Budget
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.projects?.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No projects found
                  </td>
                </tr>
              ) : (
                data?.projects?.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FiFolder className="w-5 h-5 text-blue-500 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{project.name}</div>
                          <div className="text-xs text-gray-500">
                            {project.startDate && new Date(project.startDate).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getPriorityColor(project.priority)}`}>
                        {project.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${project.completionRate}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-700">{project.completionRate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {project.completedTasks}/{project.totalTasks}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {project.actualHours}h / {project.estimatedHours}h
                      </div>
                      {project.hoursVariance !== 0 && (
                        <div className={`text-xs flex items-center ${project.hoursVariance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {project.hoursVariance > 0 ? (
                            <><FiTrendingUp className="w-3 h-3 mr-1" /> +{project.hoursVariance}h</>
                          ) : (
                            <><FiTrendingDown className="w-3 h-3 mr-1" /> {project.hoursVariance}h</>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 d-flex">
                        <span className="fs-2" > <Inr /></span>{(project?.budget_spent || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        of {(project?.budget_allocated || 0).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/app/projects/${project.id}`}
                        className="text-blue-600 hover:text-blue-900 font-medium"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Project Health Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center space-x-3 mb-4">
            <FiCheckSquare className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">On Track</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">
            {data?.projects?.filter(p => p.status === 'In Progress' && p.completionRate >= 50).length || 0}
          </p>
          <p className="text-sm text-gray-600 mt-2">Projects progressing well</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center space-x-3 mb-4">
            <FiAlertCircle className="w-6 h-6 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900">Needs Attention</h3>
          </div>
          <p className="text-3xl font-bold text-yellow-600">
            {data?.projects?.filter(p => p.status === 'On Hold' || p.status === 'Delayed').length || 0}
          </p>
          <p className="text-sm text-gray-600 mt-2">Projects requiring review</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center space-x-3 mb-4">
            <FiClock className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Completed</h3>
          </div>
          <p className="text-3xl font-bold text-blue-600">
            {data?.projects?.filter(p => p.status === 'Completed').length || 0}
          </p>
          <p className="text-sm text-gray-600 mt-2">Successfully finished</p>
        </div>
      </div>
    </div>
  );
};

export default ProjectDashboard;

